import { Component, computed, effect, inject, signal } from '@angular/core';
import { DmButton, DmIcon, DmModal, DmModalFooter, DmModalHeader } from '@digitale-menschen/component-library-angular';
import { ConfirmDialogAnswer, ConfirmDialogOptions, ConfirmDialogService } from '../../services/confirm-dialog.service';

/**
 * Renders the questions raised by `ConfirmDialogService`. Belongs in the app shell,
 * next to `dm-toaster`, so that no calling component needs markup of its own.
 *
 * Known defect in the design system, not ours: below a 480px container the footer
 * stacks with `flex-direction: column-reverse`, so the buttons are read bottom-up
 * while Tab still walks them in document order. Reading order and focus order
 * therefore disagree — WCAG 2.4.3 — and it applies to every `horizontal-fill`
 * footer in the product on a narrow screen, not only to a `compact` dialog. It has
 * to be fixed by ordering the DOM rather than reversing it in CSS, which is the
 * library's to do. Reported; do not paper over it here, or the fix will land twice.
 */
@Component({
  selector: 'lp-confirm-dialog',
  imports: [DmModal, DmModalHeader, DmModalFooter, DmButton, DmIcon],
  templateUrl: './confirm-dialog.component.html',
  styleUrl: './confirm-dialog.component.scss',
  host: { class: 'lp-confirm-dialog' },
})
export class ConfirmDialogComponent {
  private readonly confirmDialogService = inject(ConfirmDialogService);

  protected readonly isOpen = computed<boolean>(() => this.confirmDialogService.pending() !== null);
  /** Own copy of the texts, so the dialog does not sit empty during its close animation. */
  protected readonly options = signal<ConfirmDialogOptions | null>(null);

  private readonly keepOptionsWhileClosingEffect = effect(() => {
    const pending = this.confirmDialogService.pending();
    if (pending) {
      this.options.set(pending.options);
    }
  });

  /**
   * @param {ConfirmDialogAnswer} answer - Which of the three ways the question ended
   */
  protected answer(answer: ConfirmDialogAnswer): void {
    this.confirmDialogService.pending()?.answer(answer);
  }

  /**
   * Catches a close triggered by the library itself — otherwise the caller's promise
   * never resolves.
   *
   * @param {boolean} open - Whether the dialog is open
   */
  protected onOpenChange(open: boolean): void {
    if (!open) {
      this.answer('dismissed');
    }
  }

  protected onClosed(): void {
    if (!this.confirmDialogService.pending()) {
      this.options.set(null);
    }
  }
}
